import { type DragEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fileCategory, formatBytes } from '@/entities/attachment';
import {
  useDeleteWorkspaceFile,
  useWorkspaceFiles,
  useWorkspaceFileUploader,
  workspaceFileDownloadUrl,
  workspaceFilePreviewUrl,
  type WorkspaceFile,
} from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, ConfirmDialog, ErrorState, IconButton, Icons, LoadingState } from '@/shared/ui';

import styles from './WorkspaceInfo.module.css';

const ICONS = {
  image: '🖼',
  pdf: '📕',
  archive: '🗜',
  text: '📝',
  audio: '🎵',
  video: '🎬',
  document: '📄',
  other: '📎',
} as const;

/** Files of the workspace itself: upload (button or drop), open/download, remove. */
export function WorkspaceFiles({
  workspaceId,
  canManage,
}: {
  workspaceId: string;
  canManage: boolean;
}) {
  const { t, i18n } = useTranslation();
  const files = useWorkspaceFiles(workspaceId);
  const uploader = useWorkspaceFileUploader(workspaceId);
  const remove = useDeleteWorkspaceFile(workspaceId);
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [confirming, setConfirming] = useState<WorkspaceFile | null>(null);
  const list = files.data ?? [];

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setOver(false);
    uploader.add([...event.dataTransfer.files]);
  };

  return (
    <div
      className={over ? styles.drop : undefined}
      data-testid="workspace-files"
      onDragOver={
        canManage
          ? (event) => {
              event.preventDefault();
              setOver(true);
            }
          : undefined
      }
      onDragLeave={() => {
        setOver(false);
      }}
      onDrop={canManage ? onDrop : undefined}
    >
      {canManage && (
        <div className={styles.bar}>
          <Button
            size="sm"
            onClick={() => {
              input.current?.click();
            }}
          >
            <Icons.upload /> {t('workspaceHeader.files.add')}
          </Button>
          <span className={styles.progress}>{t('workspaceHeader.files.hint')}</span>
          <input
            ref={input}
            type="file"
            multiple
            className="visually-hidden"
            aria-label={t('workspaceHeader.files.add')}
            data-testid="workspace-file-input"
            onChange={(event) => {
              uploader.add([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
        </div>
      )}
      {uploader.items.map((item) => (
        <div key={item.key} className={styles.bar}>
          {item.error ? (
            <>
              <FormError error={item.error} />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  uploader.dismiss(item.key);
                }}
              >
                {t('attachments.dismiss', { name: item.name })}
              </Button>
            </>
          ) : (
            <span className={styles.progress} role="status">
              {t('attachments.progress', {
                name: item.name,
                percent: Math.round(item.progress * 100),
              })}
            </span>
          )}
        </div>
      ))}
      {files.isPending && <LoadingState />}
      {files.isError && <ErrorState error={files.error} onRetry={() => void files.refetch()} />}
      {files.isSuccess && list.length === 0 && uploader.items.length === 0 && (
        <p className={styles.empty}>{t('workspaceHeader.files.empty')}</p>
      )}
      {list.length > 0 && (
        <ul className={styles.list}>
          {list.map((file) => (
            <li key={file.id} className={styles.item} data-testid="workspace-file">
              {file.previewable ? (
                <img
                  className={styles.thumb}
                  src={workspaceFilePreviewUrl(workspaceId, file.id, 'thumb')}
                  alt=""
                  loading="lazy"
                />
              ) : (
                <span className={styles.icon} aria-hidden>
                  {ICONS[fileCategory(file.mime_type, file.filename)]}
                </span>
              )}
              <div className={styles.meta}>
                <a
                  className={styles.name}
                  title={file.filename}
                  href={
                    file.previewable
                      ? workspaceFilePreviewUrl(workspaceId, file.id, 'large')
                      : workspaceFileDownloadUrl(workspaceId, file.id)
                  }
                  {...(file.previewable
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : { download: file.filename })}
                >
                  {file.filename}
                </a>
                <span className={styles.sub}>
                  {formatBytes(file.size_bytes, i18n.language)} ·{' '}
                  {new Date(file.created_at).toLocaleDateString(i18n.language)}
                </span>
              </div>
              <div className={styles.itemActions}>
                <a
                  className={styles.iconLink}
                  href={workspaceFileDownloadUrl(workspaceId, file.id)}
                  download={file.filename}
                  aria-label={`${t('attachments.download')}: ${file.filename}`}
                  title={t('attachments.download')}
                >
                  <Icons.download />
                </a>
                {canManage && (
                  <IconButton
                    label={`${t('attachments.delete')}: ${file.filename}`}
                    icon={<Icons.trash />}
                    onClick={() => {
                      setConfirming(file);
                    }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {confirming && (
        <ConfirmDialog
          title={t('workspaceHeader.files.deleteTitle')}
          message={t('workspaceHeader.files.deleteMessage', { name: confirming.filename })}
          confirmLabel={t('attachments.delete')}
          pending={remove.isPending}
          onCancel={() => {
            setConfirming(null);
          }}
          onConfirm={() => {
            remove.mutate(confirming.id, {
              onSettled: () => {
                setConfirming(null);
              },
            });
          }}
        />
      )}
    </div>
  );
}
